import React from 'react';
import { View, Text, Image } from '@react-pdf/renderer';
import { pdfSharedStyles } from '../pdfSharedStyles';
import { safeText, formatPdfDate } from './PdfHelpers';
import { ProjectInfoSectionProps } from './types/PdfCommonTypes';

/**
 * Project Information Section Component
 * Combines project info table on the left and map on the right in a side-by-side layout
 * Provides unified project information display across all PDF templates
 * Copied from original ContainerYard template structure
 */
export const ProjectInfoSection: React.FC<ProjectInfoSectionProps> = ({ projectData, projectInfoData, t, showMap = true }) => {
    const mapImage = projectData?.mapImage;

    // Project info data array - copied from original template
    const headerProjectInfo = [
        [t('project_info_project_detail_project_name'), safeText(projectData?.projectName)],
        [t('project_info_project_detail_project_number'), safeText(projectInfoData?.data?.nrProject)],
        [t('project_info_project_detail_owner'), safeText(projectInfoData?.projectOwner)],
        [t('project_info_designer_detail_firm'), safeText(projectInfoData?.designerFirmName)],
        [t('project_info_designer_detail_country'), safeText(projectInfoData?.designerFirmCountry)],
        [t('project_info_designer_detail_city'), safeText(projectInfoData?.designerFirmCity)],
        [t('project_info_project_detail_location_latitude'), projectInfoData?.coordinates?.[0]?.toFixed(6) || 'N/A'],
        [t('project_info_project_detail_location_longitude'), projectInfoData?.coordinates?.[1]?.toFixed(6) || 'N/A'],
        [t('unit_system'), t(projectData?.unit?.toLowerCase() || 'metric')],
        [t('project_info_project_detail_date'), formatPdfDate()],
    ];

    // Helper to create project info header rows - copied from original template
    const createProjectInfoRows = (data: string[][]) => {
        return data.map((row, rowIndex) => (
            <View style={pdfSharedStyles.projectInfoRow} key={rowIndex}>
                <View style={pdfSharedStyles.projectInfoLabelCol}>
                    <Text style={pdfSharedStyles.projectInfoLabel}>{row[0]}</Text>
                </View>
                <View style={pdfSharedStyles.projectInfoValueCol}>
                    <Text style={pdfSharedStyles.projectInfoValue}>{row[1]}</Text>
                </View>
            </View>
        ));
    };

    return (
        <>
            {/* Project Information Title */}
            <Text style={pdfSharedStyles.title}>{t('pdf_project_information') || 'Project Information'}</Text>
            <View style={pdfSharedStyles.spacer1} />

            {/* Project Info Content - Table and Map side by side */}
            <View style={pdfSharedStyles.projectInfoContent}>
                {/* Project Info Table - Left Side (50% width) - copied exact structure */}
                <View style={pdfSharedStyles.projectInfoTable}>{createProjectInfoRows(headerProjectInfo)}</View>

                {/* Map Container - Right Side (50% width) */}
                {showMap && (
                    <View style={pdfSharedStyles.projectInfoMapContainer}>
                        {mapImage ? (
                            <>
                                <Image style={pdfSharedStyles.projectInfoMapImage} src={mapImage} />
                                <Text style={pdfSharedStyles.projectInfoMapLabel}>{t('project_location_map') || 'Project Location'}</Text>
                            </>
                        ) : (
                            <Text style={pdfSharedStyles.projectInfoMapLabel}>{t('no_map_available') || 'No map available'}</Text>
                        )}
                    </View>
                )}
            </View>
        </>
    );
};
